/**
 * What the new Today shows, as plain functions over the `GET /plan/today` response: the wording of each ranked
 * item (the server sends reason codes only, TR-040), the browse list grouped by theme, search and filters
 * (BR-049) and the progress marks (no scores, C-02).
 */
import { INTERFACE_TEXT } from '../constants/interfaceText';
import type { PlanContext, PlanTrack, ReadyItem, ReadyKind } from '../api/plan';

/** Fills `{name}` style placeholders in an interface text. */
export function fill(
  template: string,
  values: Record<string, string | number>
): string {
  return template.replace(/\{(\w+)\}/g, (_match, key: string) =>
    String(values[key] ?? '')
  );
}

export const KIND_LABEL: Record<ReadyKind, string> = {
  lesson: INTERFACE_TEXT.tdKindLesson,
  check_in: INTERFACE_TEXT.tdKindCheckIn,
  review: INTERFACE_TEXT.tdKindReview,
};

export function itemTitle(item: ReadyItem): string {
  const name = item.context_name ?? '';
  if (item.kind === 'check_in')
    return fill(INTERFACE_TEXT.tdTitleCheckIn, { name });
  if (item.kind === 'review') {
    const n = item.phrase_count ?? 0;
    return n === 1
      ? INTERFACE_TEXT.tdTitleReviewOne
      : fill(INTERFACE_TEXT.tdTitleReview, { n });
  }
  return fill(INTERFACE_TEXT.tdTitleLesson, {
    name,
    n: item.lesson_order ?? 1,
  });
}

/** Why the item is suggested. `blurb` is the situation's one-liner, used for a new situation. */
export function reasonText(item: ReadyItem, blurb: string): string {
  const name = item.context_name ?? '';
  const n = item.phrase_count ?? 0;
  switch (item.reason) {
    case 'check_in_finishes_situation':
      return fill(INTERFACE_TEXT.tdReasonCheckIn, { name });
    case 'continue_situation':
      return fill(INTERFACE_TEXT.tdReasonContinue, { name });
    case 'review_large':
      return fill(INTERFACE_TEXT.tdReasonReviewLarge, { n });
    case 'review_due':
      return n === 1
        ? INTERFACE_TEXT.tdReasonReviewOne
        : fill(INTERFACE_TEXT.tdReasonReview, { n });
    case 'next_situation':
      return fill(INTERFACE_TEXT.tdReasonNext, {
        blurb: blurb.charAt(0).toLowerCase() + blurb.slice(1),
      });
  }
}

export type StateFilter = 'all' | 'open' | 'in_progress' | 'passed';

export const FILTERS: { value: StateFilter; label: string }[] = [
  { value: 'all', label: INTERFACE_TEXT.tdFilterAll },
  { value: 'open', label: INTERFACE_TEXT.tdFilterOpen },
  { value: 'in_progress', label: INTERFACE_TEXT.tdFilterInProgress },
  { value: 'passed', label: INTERFACE_TEXT.tdFilterPassed },
];

/** Every situation of the returned tracks, in track order. */
export function allSituations(tracks: PlanTrack[]): PlanContext[] {
  return tracks.flatMap((track) => track.contexts);
}

export function matchesFilter(
  context: PlanContext,
  filter: StateFilter
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'open':
      return context.state === 'available' || context.state === 'in_progress';
    case 'in_progress':
      return context.state === 'in_progress';
    case 'passed':
      return context.state === 'passed';
  }
}

export function matchesQuery(context: PlanContext, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [context.context_name, context.blurb ?? '', context.theme ?? ''].some(
    (text) => text.toLowerCase().includes(q)
  );
}

export interface ThemeGroup {
  theme: string;
  situations: PlanContext[];
}

/**
 * The situations that match the search and filter, grouped by theme in the order themes first appear.
 * A situation with no theme goes under "Other", which comes last.
 */
export function groupByTheme(
  contexts: PlanContext[],
  query: string,
  filter: StateFilter
): ThemeGroup[] {
  const groups = new Map<string, PlanContext[]>();
  for (const context of contexts) {
    if (!matchesFilter(context, filter) || !matchesQuery(context, query))
      continue;
    const theme = context.theme || INTERFACE_TEXT.tdThemeOther;
    groups.set(theme, [...(groups.get(theme) ?? []), context]);
  }
  const other = INTERFACE_TEXT.tdThemeOther;
  return [...groups.entries()]
    .sort(([a], [b]) => (a === other ? 1 : 0) - (b === other ? 1 : 0))
    .map(([theme, situations]) => ({ theme, situations }));
}

export interface Marks {
  /** One entry per lesson: done or not. */
  lessons: boolean[];
  checkIn: boolean;
}

/** One mark per lesson plus one for the check-in; never a number or a score (C-02). */
export function marksFor(context: PlanContext): Marks {
  return {
    lessons: Array.from(
      { length: context.lessons_total },
      (_, i) => i < context.lessons_completed
    ),
    checkIn: context.check_in_passed ?? context.state === 'passed',
  };
}

/** Where to find the one-liner of the situation an item is about. */
export function blurbFor(item: ReadyItem, contexts: PlanContext[]): string {
  return (
    contexts.find((c) => c.context_id === item.context_id)?.blurb ??
    item.context_name ??
    ''
  );
}
